import React, { useState } from "react";
import { CircularProgress, Typography, Box } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { ReactComponent as PdfIcon } from "Assets/icons/pdf.svg";
import { ReactComponent as DownloadIcon } from "Assets/icons/downloadSlim.svg";
import { ReactComponent as DownloadMediumIcon } from "Assets/icons/downloadMedium.svg";

function FileDownload({
    attachmentId,
    fileUrl,
    contentType,
    icon: IconComponent,
    type,
    fileSize,
    fileName
}) {
    const Icon = IconComponent || PdfIcon;
    const isSmallIcon =
        IconComponent === DownloadIcon || DownloadMediumIcon;

    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);

    const handleClick = async () => {
        if (!attachmentId || !fileUrl || !fileName) {
            console.error("Missing required parameters:", { attachmentId, fileUrl, fileName });
            return;
        }

        setLoading(true);
        setProgress(0);

        const fileUrlAdr = type !== undefined && type !== null ? `${fileUrl}/${attachmentId}/${type}` : `${fileUrl}/${attachmentId}`;

        // **🔹 Estimated Response Time (Based on 600 KB/s)**
        const estimatedResponseTime = Math.max((fileSize / 600) * 2000, 10000);

        let fakeProgress = 0;
        let isRealProgress = false;

        // **Fake Progress (0 → 95%)**
        const fakeInterval = setInterval(() => {
            if (isRealProgress) return;
            fakeProgress += 1;
            setProgress(Math.min(fakeProgress, 95));
        }, estimatedResponseTime / 95);

        try {
            const response = await fetch(fileUrlAdr);
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

            clearInterval(fakeInterval);
            isRealProgress = true;
            let realProgressStart = fakeProgress;

            const data = await response.json();
            if (!data.results || !data.results[0]?.fileContent) {
                throw new Error("Invalid response: Missing file content");
            }

            const fileContentBase64 = data.results[0].fileContent;
            const byteCharacters = atob(fileContentBase64);
            const byteArrays = [];

            for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
                const slice = byteCharacters.slice(offset, offset + 1024);
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }
                byteArrays.push(new Uint8Array(byteNumbers));

                let realProgress = realProgressStart + Math.floor((offset / byteCharacters.length) * (100 - realProgressStart));
                setProgress(Math.min(realProgress, 100));
            }

            // **Correctly Create Blob from Base64**
            const blob = new Blob(byteArrays, { type: contentType || "application/pdf" });

            if (!blob.size) {
                throw new Error("Blob is empty! Something went wrong.");
            }

            // **Use Provided fileName from Props**
            const downloadUrl = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = downloadUrl;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);

            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(downloadUrl);
            }, 500);

            setProgress(100);
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            setTimeout(() => {
                setLoading(false);
                setProgress(0);
            }, 500);
        }
    };

    return (
        <>
            {contentType && (
                loading ? (
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <CircularProgress variant="determinate" value={progress} size={24} />
                        <Box
                            sx={{
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                position: 'absolute',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{ color: 'text.secondary', fontSize: "10px", fontWeight: "bold" }}
                            >
                                {progress}%
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                        <Icon
                            className="me-2 cursor-pointer"
                            onClick={handleClick}
                            style={{
                                width: isSmallIcon ? 16 : 22,
                                height: isSmallIcon ? 16 : 22,
                            }}
                        />
                )
            )}
        </>
    );
}

export default FileDownload;
